import { System, decorators, OBSERVER_TYPE, Transform } from '@combos-fun/engine';
import type { ComponentChanged } from "@combos-fun/engine";
import PhysicsEngine from './PhysicsEngine';
import { Physics } from './Physics';

export type DeepPartial<T> = {
  [P in keyof T]?: T[P] extends Object ? DeepPartial<T[P]> : T[P];
}

export interface PhysicsSystemParams {
  resolution?: number
  fps?: number
  isTest?: boolean
  element?: HTMLElement
  canvas?: HTMLCanvasElement
  deltaSampleSize?: number
  mouse?: {
    open: boolean
    constraint?: Matter.Constraint
  }
  world: DeepPartial<Matter.IWorldDefinition>
}

@decorators.componentObserver({
  Physics: [{ prop: ['bodyParams'], deep: true }],
  Transform: ['_parent'],
})
export default class PhysicsSystem extends System<PhysicsSystemParams> {
  static systemName = 'PhysicsSystem';
  private engine: PhysicsEngine;

  /**
   * System init: configure params before the game starts.
   * @param param init params
   */
  init(param?: PhysicsSystemParams) {
    this.engine = new PhysicsEngine(this.game, param);
    this.game.canvas.setAttribute('data-pixel-ratio', (param.resolution || '1') as string);
  }
  /**
   * Called when the System is installed; if the game has not started, it runs when the
   * game starts. Use it for setup work such as initializing data.
   */
  awake() { }

  /**
   * Called after the System is installed and all systems' `awake` have run.
   */
  start() {
    this.engine.start();
  }
  /**
   * Called on every game loop; can perform operations and change component properties.
   */
  update(e) {
    const changes = this.componentObserver.clear();
    for (const changed of changes) {
      if (changed) {
        this.componentChanged(changed);
      }
    }
    this.engine.update(e);
  }

  componentChanged(changed: ComponentChanged) {
    if (changed.component instanceof Physics) {
      switch (changed.type) {
        case OBSERVER_TYPE.ADD: {
          if (changed.gameObject.transform.parent && !changed.gameObject.getComponent(Physics).body) {
            this.engine.add(changed.component);
          }
          break;
        }
        case OBSERVER_TYPE.CHANGE: {
          this.engine.change(changed.component);
          break;
        }
        case OBSERVER_TYPE.REMOVE: {
          break;
        }
      }
    } else {
      switch (changed.type) {
        case OBSERVER_TYPE.CHANGE: {
          if ((changed.component as Transform).parent) {
            let physics = changed.gameObject.getComponent(Physics);
            if (physics && !physics.body) {
              this.engine.add(physics);
            }
          } else {
            let physics = changed.gameObject.getComponent(Physics);
            physics && this.engine.remove(physics);
          }
        }
      }
    }
  }
  /**
   * Like `update`, but called after every System's and component's `update` has run.
   */
  lateUpdate() { }
  /**
   * Called when the game starts or resumes playing after a pause.
   */
  onResume() {
    if (!this.engine.enabled) {
      this.engine.awake();
    }
  }
  /**
   * Called when the game is paused.
   */
  onPause() {
    this.engine.stop();
  }
  /**
   * Called when the system is destroyed.
   */
  onDestroy() {
    this.engine?.destroy();
  }
}
